import React from 'react';
import {
  Box,
  Typography,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import { RefreshCw } from 'lucide-react';

/**
 * Minimal-style section toolbar: title block + optional chip + range toggles + refresh.
 */
const DashboardSectionHeader = ({
  title,
  subtitle,
  chip,
  ranges = [],
  rangeValue,
  onRangeChange,
  onRefresh,
  refreshTooltip = 'Refresh',
  action,
}) => {
  const theme = useTheme();

  const pillToggleSx = {
    bgcolor: alpha(theme.palette.grey[500], 0.08),
    borderRadius: 2,
    p: 0.5,
    flexWrap: 'wrap',
    '& .MuiToggleButtonGroup-grouped': {
      border: 0,
      borderRadius: '10px !important',
      mx: 0.25,
      px: { xs: 1.75, sm: 2.25 },
      py: 0.75,
      fontWeight: 700,
      fontSize: 13,
      lineHeight: 1.2,
      color: 'text.secondary',
      textTransform: 'none',
      '&.Mui-selected': {
        bgcolor: 'background.paper',
        color: 'primary.main',
        boxShadow: `0 1px 4px ${alpha(theme.palette.grey[900], 0.12)}`,
        '&:hover': { bgcolor: 'background.paper' },
      },
      '&:hover': {
        bgcolor: alpha(theme.palette.grey[500], 0.06),
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 2.5 },
        mb: 2.5,
        p: { xs: 2, sm: 2.25, md: 2.5 },
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 1px 2px ${alpha(theme.palette.grey[900], 0.04)}`,
        width: '100%',
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.125rem' }, lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.35, fontWeight: 500, fontSize: { xs: 13, md: 14 } }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ flexShrink: 1, width: { xs: '100%', sm: 'auto' } }}
      >
        {chip}
        {action}
        {ranges.length > 0 && (
          <ToggleButtonGroup
            value={rangeValue}
            exclusive
            size="small"
            onChange={(_, v) => v != null && onRangeChange?.(v)}
            sx={pillToggleSx}
          >
            {ranges.map((r) => (
              <ToggleButton key={r.value} value={r.value}>
                {r.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {onRefresh && (
          <Tooltip title={refreshTooltip}>
            <IconButton
              onClick={onRefresh}
              size="small"
              aria-label={refreshTooltip}
              sx={{
                width: 40,
                height: 40,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.paper',
                color: 'text.secondary',
                flexShrink: 0,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: 'primary.main',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
              }}
            >
              <RefreshCw size={18} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

export default DashboardSectionHeader;
